<script setup>
/* eslint-disable no-console */
import { watch, computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { consultaDetalleContratoJson } from 'dashboard/api/cea';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const router = useRouter();
const route = useRoute();

const contactGetter = useMapGetter('contacts/getContact');
const contact = computed(() => contactGetter.value(props.contactId));
const contractNumber = computed(
  () => contact.value?.custom_attributes?.contrato
);

const isLoading = ref(false);
const contractData = ref(null);

const isActive = computed(
  () => contractData.value && !contractData.value.contrato?.fechaBaja
);

const titular = computed(() => {
  const c = contractData.value?.contrato;
  const dp = contractData.value?.datosPersonales;
  return c?.titular || dp?.titular || null;
});

const address = computed(() => {
  const ps = contractData.value?.puntoSuministro;
  if (!ps) return null;
  const parts = [ps.calle, ps.numero].filter(Boolean);
  if (ps.municipio) parts.push(ps.municipio);
  return parts.join(' ').replace(/\s+,/, ',').trim() || null;
});

const useType = computed(() => contractData.value?.contrato?.descUso || null);
const meterNumber = computed(
  () => contractData.value?.contrato?.numeroContador || null
);

const fetchContractDetail = async () => {
  if (!contractNumber.value) {
    contractData.value = null;
    return;
  }

  isLoading.value = true;
  try {
    const detail = await consultaDetalleContratoJson(
      contractNumber.value,
      'es'
    );
    contractData.value = detail?.GenericoContratoDTO || detail;
  } catch (error) {
    console.error('Error fetching CEA contract detail:', error);
    contractData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const viewContract = () => {
  const num = contractNumber.value;
  if (!num) return;

  const explotacion = contractData.value?.contrato?.explotacion;
  if (explotacion) {
    localStorage.setItem(`contrato_${num}_explotacion`, explotacion);
  }

  const accountId = route.params.accountId;
  router
    .push({
      name: 'contract_detail',
      params: { accountId, contratoId: num },
    })
    .catch(() => {
      router.push(`/app/accounts/${accountId}/contracts/${num}`);
    });
};

watch(
  contractNumber,
  newVal => {
    if (newVal) {
      fetchContractDetail();
    } else {
      contractData.value = null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="contractNumber" class="flex flex-col">
      <div
        class="flex items-center justify-between px-4 py-3 hover:bg-n-alpha-1 cursor-pointer"
        @click="viewContract"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              #{{ contractNumber }}
            </span>
            <span
              class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
              :class="
                isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              "
            >
              {{ isActive ? 'Activo' : 'Inactivo' }}
            </span>
          </div>
          <span v-if="titular" class="text-xs text-n-slate-11 truncate">
            {{ titular }}
          </span>
          <span
            v-if="address"
            class="flex items-center gap-1 text-xs text-n-slate-11 truncate"
          >
            <i class="i-lucide-map-pin size-3 flex-shrink-0" />
            {{ address }}
          </span>
          <div
            v-if="useType || meterNumber"
            class="flex items-center gap-1 text-xs text-n-slate-11"
          >
            <span v-if="useType">Uso: {{ useType }}</span>
            <span v-if="useType && meterNumber">&middot;</span>
            <span v-if="meterNumber">Contador: {{ meterNumber }}</span>
          </div>
        </div>
        <i class="i-lucide-chevron-right text-n-slate-11 flex-shrink-0" />
      </div>
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      No hay contratos para este contacto
    </p>
  </div>
</template>
